import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./ResetPassword.module.css";

// Import des icônes
import passwordIcon from "./svg/password.svg";
import { ReactComponent as PasswordSVG } from "./svg/password2.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

// Import du thunk et du selector du slice
import { fetchResetPassword, resetPasswordSelector, resetPasswordClear } from "./resetPasswordSlice";

const ResetPassword = () => {
  const dispatch = useDispatch();
  const utilisateur = useSelector((state) => state.connexion.utilisateur);
  const { loading, hasErrors, success, message } = useSelector(resetPasswordSelector);

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [localError, setLocalError] = useState("");

  // Nouveaux états pour la visibilité des mots de passe
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Réinitialise l'état du slice au montage du composant
  useEffect(() => {
    dispatch(resetPasswordClear());
  }, [dispatch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Vérifications locales
    if (!oldPassword) {
      setLocalError("Veuillez saisir votre ancien mot de passe.");
      return;
    }
    if (!newPassword) {
      setLocalError("Veuillez saisir un nouveau mot de passe.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setLocalError("Les nouveaux mots de passe ne correspondent pas.");
      return;
    }
    const regex = /^.{8,}$/;
    if (!regex.test(newPassword)) {
      setLocalError("Le nouveau mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    setLocalError("");
    // Dispatch de l'appel API pour changer le mot de passe
    dispatch(fetchResetPassword({ id: utilisateur.id, oldPassword, newPassword }));
  };

  // Calcul de la force du mot de passe (score de 0 à 100)
  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[A-Z]/.test(password)) strength += 20;
    if (/[a-z]/.test(password)) strength += 15;
    if (/\d/.test(password)) strength += 20;
    if (/[^A-Za-z0-9]/.test(password)) strength += 20;
    return Math.min(strength, 100);
  };

  const getStrengthLabel = (score) => {
    if (score < 40) return "Faible";
    if (score < 70) return "Moyen";
    return "Fort";
  };

  const strengthScore = getPasswordStrength(newPassword);

  // Détermine si le style d'erreur doit être appliqué
  const isNewPasswordInvalid = newPassword && newPassword.length < 8;
  const isConfirmPasswordInvalid = confirmPassword && newPassword !== confirmPassword;

  // Fonctions de toggle pour la visibilité
  const toggleNewPasswordVisibility = () => {
    setShowNewPassword((prev) => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const applyShake = utilisateur.type === "utilisateur" && !loading && !success;

  return (
    <div className={styles.container}>
      {/* Conteneur pour les icônes */}
      <div className={styles.iconContainer}>
        <img src={passwordIcon} alt="Icône de mot de passe" className={styles.icon} />
        <PasswordSVG
          className={`${styles.passwordSvg} ${loading ? styles.animate : ""} ${applyShake ? styles.shake : ""}`}
        />
      </div>

      {/* Affichage des messages */}
      {success && message && (
        <div className={styles.successBanner}>{message}</div>
      )}
      {(localError || (hasErrors && message)) && (
        <div className={styles.errorBanner}>
          {localError || message}
        </div>
      )}

      {/* Formulaire */}
      <form className={styles.form} onSubmit={handleSubmit}>
        <input
          type="password"
          placeholder="Ancien mot de passe"
          className={styles.input}
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
          autoComplete="current-password"
        />

        {/* Champ Nouveau mot de passe avec œilleton */}
        <div className={styles.passwordContainer}>
          <input
            type={showNewPassword ? "text" : "password"}
            placeholder="Nouveau mot de passe"
            className={`${styles.input} ${isNewPasswordInvalid ? styles.errorInput : ""}`}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
          />
          <FontAwesomeIcon
            icon={showNewPassword ? faEyeSlash : faEye}
            onClick={toggleNewPasswordVisibility}
            className={styles.eyeIcon}
          />
        </div>
        {isNewPasswordInvalid && (
          <small className={styles.inputErrorMessage}>
            Le nouveau mot de passe doit contenir au moins 8 caractères.
          </small>
        )}

        {/* Barre de force du mot de passe */}
        {newPassword && (
          <>
            <div className={styles.strengthContainer}>
              <div className={styles.strengthBar}>
                <div className={styles.strengthFill} style={{ width: `${strengthScore}%` }} />
              </div>
              <span
                className={styles.strengthLabel}
                title="Pour augmenter la complexité, utilisez des majuscules, chiffres et caractères spéciaux."
              >
                {getStrengthLabel(strengthScore)}
              </span>
            </div>
            <div className={styles.strengthComment}>
              Pour augmenter la complexité, utilisez des majuscules, chiffres et caractères spéciaux.
            </div>
          </>
        )}

        {/* Champ Répéter le nouveau mot de passe avec œilleton */}
        <div className={styles.passwordContainer}>
          <input
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Répéter le nouveau mot de passe"
            className={`${styles.input} ${isConfirmPasswordInvalid ? styles.errorInput : ""}`}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
          />
          <FontAwesomeIcon
            icon={showConfirmPassword ? faEyeSlash : faEye}
            onClick={toggleConfirmPasswordVisibility}
            className={styles.eyeIcon}
          />
        </div>
        {isConfirmPasswordInvalid && (
          <small className={styles.inputErrorMessage}>
            Les mots de passe ne correspondent pas.
          </small>
        )}

        <button type="submit" className={styles.button} disabled={loading}>
          {loading ? "Chargement..." : "Valider"}
        </button>
      </form>
    </div>
  );
};

export default ResetPassword;
